import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import { Card, Button, Badge, Tabs } from '../components/ui';
import { 
  Droplets, 
  Sun, 
  Shield,
  Clock,
  CheckCircle,
  AlertCircle,
  Heart,
  Loader2,
  AlertTriangle,
  Activity,
  Thermometer,
  Zap
} from 'lucide-react';
import { healthConditions, healingStages, emergencySymptoms } from '../data/healthInfo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Care = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'tr';
  const L = (obj) => obj[lang] || obj['tr'] || obj['en'];
  const [tattoos, setTattoos] = useState([]);
  const [selectedTattoo, setSelectedTattoo] = useState(null);
  const [careLogs, setCareLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('guide');

  const tabs = [
    { id: 'guide', label: 'Bakım Rehberi' },
    { id: 'health', label: 'Sağlık Bilgileri' },
    { id: 'logs', label: 'Bakım Kayıtları' },
  ];

  const careGuide = [
    {
      phase: 'İlk 1-3 Gün',
      icon: Shield,
      color: 'text-red-400',
      tips: [
        'Sargiyi 2-4 saat sonra çıkarın',
        'Hafif, parfümsüz sabunla yıkayın',
        'Kuru ve temiz tutun',
        'Sıkı kıyafetlerden kaçının'
      ]
    },
    {
      phase: '4-14 Gün',
      icon: Droplets,
      color: 'text-blue-400',
      tips: [
        'Günde 2-3 kez nemlendirin',
        'İnce tabaka halinde losyon uygulayın',
        'Kabukları koparmayın',
        'Havuza ve denize girmeyin'
      ]
    },
    {
      phase: '2-4 Hafta',
      icon: Sun,
      color: 'text-yellow-400',
      tips: [
        'Doğrudan güneşten kaçının',
        'SPF 50+ güneş kremi kullanın',
        'Nemlendirmeye devam edin',
        'Kaşıntı normal, kaşımayın'
      ]
    },
    {
      phase: 'Uzun Vadeli',
      icon: Heart,
      color: 'text-primary',
      tips: [
        'Düzenli nemlendirme',
        'Her zaman güneş koruması',
        'Yıllık renk kontrolü',
        'Gerekirse rötuş yaptırın'
      ]
    }
  ];

  const quickCareActions = [
    { id: 'moisturized', icon: Droplets, label: 'Nemlendirdim', color: 'bg-blue-500/20 text-blue-400' },
    { id: 'applied_sunscreen', icon: Sun, label: 'Güneş Kremi', color: 'bg-yellow-500/20 text-yellow-400' },
    { id: 'washed', icon: Shield, label: 'Yıkadım', color: 'bg-green-500/20 text-green-400' },
  ];

  useEffect(() => {
    fetchTattoos();
  }, []);

  useEffect(() => {
    if (selectedTattoo) {
      fetchCareLogs(selectedTattoo.tattoo_id);
    }
  }, [selectedTattoo]);

  const fetchTattoos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tattoos`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTattoos(data);
        if (data.length > 0) {
          setSelectedTattoo(data[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch tattoos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCareLogs = async (tattooId) => {
    try {
      const response = await fetch(`${API_URL}/api/tattoos/${tattooId}/care-logs`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCareLogs(data);
      }
    } catch (error) {
      console.error('Failed to fetch care logs:', error);
    }
  };

  const logCareAction = async (action) => {
    if (!selectedTattoo) return;

    try {
      const response = await fetch(`${API_URL}/api/tattoos/${selectedTattoo.tattoo_id}/care-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action }),
      });

      if (response.ok) {
        fetchCareLogs(selectedTattoo.tattoo_id);
      }
    } catch (error) {
      console.error('Failed to log care action:', error);
    }
  };

  const getHealingDays = (tattoo) => {
    if (!tattoo?.healing_started) return 0;
    const start = new Date(tattoo.healing_started);
    const now = new Date();
    return Math.floor((now - start) / (1000 * 60 * 60 * 24));
  };

  const getHealingProgress = (tattoo) => {
    const days = getHealingDays(tattoo);
    if (days >= 30) return 100;
    return Math.min(Math.round((days / 30) * 100), 100);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl mb-1" data-testid="care-title">
            Bakım <span className="text-primary">Merkezi</span>
          </h1>
          <p className="text-muted-foreground">
            Dövmelerinizi en iyi şekilde koruyun
          </p>
        </div>

        {/* Tattoo Selector */}
        {tattoos.length > 0 && (
          <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
            {tattoos.filter(t => t.healing_status === 'healing').map((tattoo) => (
              <button
                key={tattoo.tattoo_id}
                onClick={() => setSelectedTattoo(tattoo)}
                className={`flex-shrink-0 px-4 py-2 rounded-sm border transition-colors ${
                  selectedTattoo?.tattoo_id === tattoo.tattoo_id
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/50'
                }`}
                data-testid={`tattoo-selector-${tattoo.tattoo_id}`}
              >
                {tattoo.name}
              </button>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        {selectedTattoo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h2 className="font-heading text-xl">{selectedTattoo.name}</h2>
                  <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                    <span>Gün {getHealingDays(selectedTattoo)}</span>
                    <span>•</span>
                    <span>%{getHealingProgress(selectedTattoo)} İyileşme</span>
                  </div>
                </div>
                <Badge variant={selectedTattoo.healing_status === 'healed' ? 'success' : 'warning'}>
                  {selectedTattoo.healing_status === 'healed' ? 'İyileşti' : 'İyileşiyor'}
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {quickCareActions.map((action) => {
                  const Icon = action.icon;
                  const todayLog = careLogs.find(
                    log => log.action === action.id && 
                    new Date(log.created_at).toDateString() === new Date().toDateString()
                  );
                  
                  return (
                    <Button
                      key={action.id}
                      variant={todayLog ? 'default' : 'outline'}
                      className="h-auto py-4 flex flex-col items-center gap-2"
                      onClick={() => !todayLog && logCareAction(action.id)}
                      disabled={!!todayLog}
                      data-testid={`care-action-${action.id}`}
                    >
                      <div className={`w-10 h-10 rounded-sm ${todayLog ? 'bg-primary/20' : action.color} flex items-center justify-center`}>
                        {todayLog ? (
                          <CheckCircle className="w-5 h-5 text-primary" />
                        ) : (
                          <Icon className="w-5 h-5" />
                        )}
                      </div>
                      <span className="text-xs">{action.label}</span>
                    </Button>
                  );
                })}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Tabs */}
        <Tabs 
          tabs={tabs} 
          activeTab={activeTab} 
          onTabChange={setActiveTab}
          className="mb-8"
        />

        {/* Tab Content */}
        {activeTab === 'guide' ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {careGuide.map((phase, index) => {
              const Icon = phase.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-6 h-full" data-testid={`care-phase-${index}`}>
                    <div className={`w-12 h-12 rounded-sm bg-muted flex items-center justify-center mb-4`}>
                      <Icon className={`w-6 h-6 ${phase.color}`} />
                    </div>
                    <h3 className="font-heading text-lg mb-3">{phase.phase}</h3>
                    <ul className="space-y-2">
                      {phase.tips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        ) : activeTab === 'health' ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            {/* Emergency Warning */}
            <Card className="p-6 border-red-500/50 bg-red-500/10">
              <div className="flex gap-4">
                <AlertTriangle className="w-8 h-8 text-red-500 flex-shrink-0" />
                <div>
                  <h3 className="font-heading text-lg mb-3 text-red-400">
                    {L(emergencySymptoms).title}
                  </h3>
                  <ul className="space-y-2 mb-4">
                    {L(emergencySymptoms).symptoms.map((symptom, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-red-300">
                        <AlertCircle className="w-4 h-4" />
                        {symptom}
                      </li>
                    ))}
                  </ul>
                  <p className="text-red-400 font-bold">{L(emergencySymptoms).action}</p>
                </div>
              </div>
            </Card>

            {/* Health Conditions */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Infection */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-sm bg-red-500/20 flex items-center justify-center">
                    <Thermometer className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg">{L(healthConditions.infection).name}</h3>
                    <Badge variant="destructive">Kritik</Badge>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {L(healthConditions.infection).description}
                </p>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium mb-2 text-red-400">Belirtiler:</h4>
                    <ul className="space-y-1">
                      {L(healthConditions.infection).symptoms.map((s, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-red-400" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium mb-2 text-green-400">Tedavi:</h4>
                    <ul className="space-y-1">
                      {L(healthConditions.infection).treatment.map((t, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>

              {/* Allergic Reaction */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-sm bg-yellow-500/20 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg">{L(healthConditions.allergicReaction).name}</h3>
                    <Badge variant="warning">Orta</Badge>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {L(healthConditions.allergicReaction).description}
                </p>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium mb-2 text-yellow-400">Belirtiler:</h4>
                    <ul className="space-y-1">
                      {L(healthConditions.allergicReaction).symptoms.map((s, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-yellow-400" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium mb-2 text-blue-400">Yaygın Alerjenler:</h4>
                    <div className="flex flex-wrap gap-2">
                      {L(healthConditions.allergicReaction).commonAllergens.map((a, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-muted rounded-sm">
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Scarring */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-sm bg-purple-500/20 flex items-center justify-center">
                    <Activity className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg">{L(healthConditions.scarring).name}</h3>
                    <Badge>Orta</Badge>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {L(healthConditions.scarring).description}
                </p>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium mb-2 text-purple-400">Skar Tipleri:</h4>
                    {L(healthConditions.scarring).types.map((type, i) => (
                      <div key={i} className="mb-2 p-2 bg-muted/50 rounded-sm">
                        <p className="text-xs font-medium">{type.name}</p>
                        <p className="text-xs text-muted-foreground">{type.description}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium mb-2 text-green-400">Önleme:</h4>
                    <ul className="space-y-1">
                      {L(healthConditions.scarring).prevention.map((p, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>

              {/* Blowout */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-sm bg-blue-500/20 flex items-center justify-center">
                    <Droplets className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg">{L(healthConditions.blowout).name}</h3>
                    <Badge variant="outline">Düşük</Badge>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {L(healthConditions.blowout).description}
                </p>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium mb-2 text-blue-400">Belirtiler:</h4>
                    <ul className="space-y-1">
                      {L(healthConditions.blowout).symptoms.map((s, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-blue-400" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium mb-2 text-orange-400">Nedenleri:</h4>
                    <ul className="space-y-1">
                      {L(healthConditions.blowout).causes.map((c, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                          <AlertCircle className="w-3 h-3 text-orange-400" />
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            </div>

            {/* Healing Stages Timeline */}
            <Card className="p-6">
              <h3 className="font-heading text-xl mb-6">İyileşme Aşamaları</h3>
              <div className="space-y-6">
                {L(healingStages).map((stage, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
                        {stage.day}
                      </div>
                      {index < L(healingStages).length - 1 && (
                        <div className="w-0.5 h-full bg-primary/20 mt-2" />
                      )}
                    </div>
                    <div className="flex-1 pb-6">
                      <h4 className="font-medium mb-1">{stage.name}</h4>
                      <p className="text-sm text-muted-foreground mb-3">{stage.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {stage.care.map((tip, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-sm">
                            {tip}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {careLogs.length > 0 ? (
              <div className="space-y-4">
                {careLogs.map((log, index) => {
                  const actionInfo = quickCareActions.find(a => a.id === log.action);
                  const Icon = actionInfo?.icon || Clock;
                  
                  return (
                    <Card key={log.log_id} className="p-4" data-testid={`care-log-${index}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-sm ${actionInfo?.color || 'bg-muted'} flex items-center justify-center`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{actionInfo?.label || log.action}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(log.created_at).toLocaleString('tr-TR')}
                          </p>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-lg mb-2">Henüz Kayıt Yok</h3>
                <p className="text-muted-foreground">
                  Bakım eylemlerinizi kaydetmeye başlayın.
                </p>
              </Card>
            )}
          </motion.div>
        )}

        {/* Tips Alert */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <Card className="p-6 border-primary/30 bg-primary/5">
            <div className="flex gap-4">
              <AlertCircle className="w-6 h-6 text-primary flex-shrink-0" />
              <div>
                <h3 className="font-heading text-lg mb-2">Önemli Uyarı</h3>
                <p className="text-muted-foreground text-sm">
                  İyileşme sürecinde enfeksiyon belirtileri (aşırı kızarıklık, şişlik, ateş, sarı/yeşil akıntı) 
                  görürseniz derhal bir sağlık uzmanına başvurun. Dövmenizi temiz tutun ve 
                  yüzme havuzlarından uzak durun.
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default Care;
